// CalendarView.jsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEvents, getAllSellers, deleteEvent, changeEventStatus } from '../../Redux/Actions/actions';
import { useNavigate } from 'react-router-dom';
import styles from './CalendarView.module.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';

moment.locale('es');
const localizer = momentLocalizer(moment);

const TIPO_COLORS = {
  reunion: '#2196F3',
  visita: '#4CAF50',
  recordatorio: '#FF9800',
  vencimiento: '#F44336',
  personal: '#9C27B0',
  otro: '#607D8B',
};

const TIPO_LABELS = {
  reunion: 'Reunión',
  visita: 'Visita',
  recordatorio: 'Recordatorio',
  vencimiento: 'Vencimiento',
  personal: 'Personal',
  otro: 'Otro',
};

const ESTADO_LABELS = {
  pendiente: 'Pendiente',
  confirmado: 'Confirmado',
  completado: 'Completado',
  cancelado: 'Cancelado',
};

const PRIORIDAD_LABELS = {
  baja: '🟢 Baja',
  media: '🟡 Media',
  alta: '🟠 Alta',
  urgente: '🔴 Urgente',
};

const allowedEventEmails = [
  'arosa@byraices.com',
  'tsanesteban@byraices.com',
  'dsanesteban@byraices.com',
  'jgarcia@byraices.com',
  'mgonella@byraices.com',
  'agutierrez@byraices.com',
  "matencia@byraices.com"
];

const messages = {
  allDay: 'Todo el día',
  previous: '← Anterior',
  next: 'Siguiente →',
  today: 'Hoy',
  month: 'Mes',
  week: 'Semana',
  day: 'Día',
  agenda: 'Agenda',
  date: 'Fecha',
  time: 'Hora',
  event: 'Evento',
  noEventsInRange: 'No hay eventos en este rango.',
  showMore: (total) => `+${total} más`,
};

function toISODate(fecha) {
  if (!fecha) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(fecha)) return fecha.slice(0, 10);
  const [d, m, y] = fecha.split('/');
  const year = y.length === 2 ? '20' + y : y;
  return `${year}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

const CalendarView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const eventos = useSelector(state => state.events);
  const loading = useSelector(state => state.eventsLoading);
  const user = useSelector(state => state.user);
  const sellersRaw = useSelector(state => state.sellers);
  const sellers = Array.isArray(sellersRaw) ? sellersRaw : (Array.isArray(sellersRaw?.data) ? sellersRaw.data : []);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterTipo, setFilterTipo] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterAgente, setFilterAgente] = useState('');
  const [currentView, setCurrentView] = useState('month');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dayPanel, setDayPanel] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const isAdmin = user?.user?.mail && allowedEventEmails.includes(user.user.mail);

  useEffect(() => {
    dispatch(fetchEvents());
    dispatch(getAllSellers('active'));
  }, [dispatch]);

  const calendarEvents = useMemo(() => {
    let filtered = Array.isArray(eventos) ? eventos : [];
    if (filterTipo) filtered = filtered.filter(ev => ev.tipo === filterTipo);
    if (filterEstado) filtered = filtered.filter(ev => ev.estado === filterEstado);
    if (filterAgente) filtered = filtered.filter(ev =>
      ev.agentes?.some(a => a.id === Number(filterAgente))
    );

    return filtered.map(ev => {
      const fechaISO = toISODate(ev.fecha);
      const start = ev.horaInicio
        ? new Date(`${fechaISO}T${ev.horaInicio}`)
        : new Date(`${fechaISO}T00:00:00`);
      const end = ev.horaFin
        ? new Date(`${fechaISO}T${ev.horaFin}`)
        : new Date(`${fechaISO}T23:59:59`);
      return {
        id: ev.id,
        title: ev.titulo,
        start,
        end,
        allDay: ev.todoElDia || false,
        resource: ev,
      };
    });
  }, [eventos, filterTipo, filterEstado, filterAgente]);

  const eventStyleGetter = useCallback((event) => {
    const tipo = event.resource?.tipo || 'otro';
    const color = event.resource?.color || TIPO_COLORS[tipo] || TIPO_COLORS.otro;
    const isCancelled = event.resource?.estado === 'cancelado';
    return {
      style: {
        backgroundColor: color,
        borderRadius: '6px',
        opacity: isCancelled ? 0.5 : 1,
        color: '#fff',
        border: 'none',
        padding: '2px 6px',
        fontSize: '0.85rem',
        fontWeight: 600,
        textDecoration: isCancelled ? 'line-through' : 'none',
      },
    };
  }, []);

  const handleSelectEvent = useCallback((event) => {
    setDayPanel(null);
    setSelectedEvent(event.resource);
  }, []);

  const handleSelectSlot = useCallback(({ start }) => {
    if (!isAdmin) return;
    const dayStart = new Date(start);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(start);
    dayEnd.setHours(23, 59, 59, 999);
    const allEvents = Array.isArray(eventos) ? eventos : [];
    const dayEvents = allEvents.filter(ev => {
      const fechaISO = toISODate(ev.fecha);
      if (!fechaISO) return false;
      const evDate = new Date(fechaISO + 'T12:00:00');
      return evDate >= dayStart && evDate <= dayEnd;
    });
    setDayPanel({ date: start, events: dayEvents });
  }, [isAdmin, eventos]);

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('¿Estás seguro de que querés eliminar este evento?')) return;
    setDeleting(id);
    try {
      await dispatch(deleteEvent(id));
      if (selectedEvent?.id === id) setSelectedEvent(null);
      if (dayPanel) {
        setDayPanel(prev => prev ? { ...prev, events: prev.events.filter(e => e.id !== id) } : null);
      }
    } catch (err) {
      alert('Error al eliminar el evento');
    }
    setDeleting(null);
  };

  const handleChangeStatus = async (id, estado) => {
    try {
      await dispatch(changeEventStatus(id, estado));
      dispatch(fetchEvents());
    } catch (err) {
      alert('Error al cambiar estado');
    }
  };

  const formatDate = (fecha) => {
    const iso = toISODate(fecha);
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <div className={styles.calendarContainer}>
      <div className={styles.header}>
        <h2 className={styles.title}>Calendario de Eventos</h2>
        {isAdmin && (
          <button className={styles.createBtn} onClick={() => navigate('/eventos/crear')}>
            + Crear Evento
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <label>Tipo</label>
          <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(TIPO_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div className={styles.filterGroup}>
          <label>Estado</label>
          <select value={filterEstado} onChange={e => setFilterEstado(e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(ESTADO_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        {isAdmin && sellers.length > 0 && (
          <div className={styles.filterGroup}>
            <label>Agente</label>
            <select value={filterAgente} onChange={e => setFilterAgente(e.target.value)}>
              <option value="">Todos</option>
              {sellers.map(s => (
                <option key={s.id} value={s.id}>{s.name} {s.last_name}</option>
              ))}
            </select>
          </div>
        )}
        {/* Leyenda de colores */}
        <div className={styles.legend}>
          {Object.entries(TIPO_COLORS).map(([tipo, color]) => (
            <span key={tipo} className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: color }} />
              {TIPO_LABELS[tipo]}
            </span>
          ))}
        </div>
      </div>

      {loading && <div className={styles.loading}>Cargando eventos...</div>}

      {/* Calendario */}
      <div className={styles.calendarWrapper}>
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 700 }}
          messages={messages}
          culture="es"
          views={['month', 'week', 'day', 'agenda']}
          view={currentView}
          onView={setCurrentView}
          date={currentDate}
          onNavigate={setCurrentDate}
          eventPropGetter={eventStyleGetter}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={isAdmin ? handleSelectSlot : undefined}
          popup
          selectable={isAdmin}
        />
      </div>

      {/* Panel de eventos del día (admin) */}
      {dayPanel && isAdmin && (
        <div className={styles.modalOverlay} onClick={() => setDayPanel(null)}>
          <div className={styles.dayPanelModal} onClick={e => e.stopPropagation()}>
            <button className={styles.closeBtn} onClick={() => setDayPanel(null)}>✕</button>
            <h3 className={styles.dayPanelTitle}>
              📅 {moment(dayPanel.date).format('dddd D [de] MMMM [de] YYYY')}
            </h3>
            <button className={styles.createBtnSmall} onClick={() => { setDayPanel(null); navigate('/eventos/crear'); }}>
              + Crear evento en este día
            </button>
            {dayPanel.events.length === 0 ? (
              <div className={styles.dayPanelEmpty}>No hay eventos este día</div>
            ) : (
              <div className={styles.dayPanelList}>
                {dayPanel.events.map(ev => (
                  <div key={ev.id} className={styles.dayPanelItem}>
                    <div className={styles.dayPanelItemHeader}>
                      <span className={styles.tipoBadge} style={{ background: ev.color || TIPO_COLORS[ev.tipo] || TIPO_COLORS.otro }}>
                        {TIPO_LABELS[ev.tipo] || 'Otro'}
                      </span>
                      <span className={styles.estadoBadge} data-estado={ev.estado}>
                        {ESTADO_LABELS[ev.estado] || 'Pendiente'}
                      </span>
                      {ev.horaInicio && <span className={styles.dayPanelTime}>⏰ {ev.horaInicio}{ev.horaFin ? ` - ${ev.horaFin}` : ''}</span>}
                    </div>
                    <div className={styles.dayPanelItemTitle}>{ev.titulo}</div>
                    {ev.descripcion && <div className={styles.dayPanelItemDesc}>{ev.descripcion}</div>}
                    {ev.agentes && ev.agentes.length > 0 && (
                      <div className={styles.dayPanelAgentes}>
                        👥 {ev.agentes.map(a => `${a.name} ${a.last_name}`).join(', ')}
                      </div>
                    )}
                    <div className={styles.dayPanelItemActions}>
                      <button className={styles.editBtnSmall} onClick={() => { setDayPanel(null); navigate(`/eventos/editar/${ev.id}`); }}>
                        ✏️ Editar
                      </button>
                      <select
                        className={styles.statusSelectSmall}
                        value={ev.estado || 'pendiente'}
                        onChange={e => handleChangeStatus(ev.id, e.target.value)}
                      >
                        {Object.entries(ESTADO_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                      <button
                        className={styles.deleteBtnSmall}
                        onClick={() => handleDeleteEvent(ev.id)}
                        disabled={deleting === ev.id}
                      >
                        {deleting === ev.id ? '...' : '🗑️'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de detalle */}
      {selectedEvent && (
        <div className={styles.modalOverlay} onClick={() => setSelectedEvent(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <button className={styles.closeBtn} onClick={() => setSelectedEvent(null)}>✕</button>

            <div className={styles.modalHeader}>
              <span
                className={styles.tipoBadge}
                style={{ background: selectedEvent.color || TIPO_COLORS[selectedEvent.tipo] || TIPO_COLORS.otro }}
              >
                {TIPO_LABELS[selectedEvent.tipo] || 'Otro'}
              </span>
              <span className={styles.prioridadBadge}>
                {PRIORIDAD_LABELS[selectedEvent.prioridad] || '🟡 Media'}
              </span>
              <span className={styles.estadoBadge} data-estado={selectedEvent.estado}>
                {ESTADO_LABELS[selectedEvent.estado] || 'Pendiente'}
              </span>
            </div>

            <h3 className={styles.modalTitle}>{selectedEvent.titulo}</h3>

            {selectedEvent.descripcion && (
              <p className={styles.modalDesc}>{selectedEvent.descripcion}</p>
            )}

            <div className={styles.modalInfo}>
              <div className={styles.modalInfoItem}>
                <span className={styles.modalLabel}>📅 Fecha</span>
                <span>{formatDate(selectedEvent.fecha)}</span>
              </div>
              {selectedEvent.horaInicio && (
                <div className={styles.modalInfoItem}>
                  <span className={styles.modalLabel}>⏰ Horario</span>
                  <span>{selectedEvent.horaInicio}{selectedEvent.horaFin ? ` - ${selectedEvent.horaFin}` : ''}</span>
                </div>
              )}
              {selectedEvent.ubicacion && (
                <div className={styles.modalInfoItem}>
                  <span className={styles.modalLabel}>📍 Ubicación</span>
                  <span>{selectedEvent.ubicacion}</span>
                </div>
              )}
              {selectedEvent.todoElDia && (
                <div className={styles.modalInfoItem}>
                  <span className={styles.modalLabel}>🕐 Todo el día</span>
                  <span>Sí</span>
                </div>
              )}
            </div>

            {selectedEvent.notas && (
              <div className={styles.modalNotas}>
                <strong>Notas:</strong> {selectedEvent.notas}
              </div>
            )}

            {selectedEvent.agentes && selectedEvent.agentes.length > 0 && (
              <div className={styles.modalAgentes}>
                <strong>Agentes asignados:</strong>
                <ul className={styles.agentesList}>
                  {selectedEvent.agentes.map(a => (
                    <li key={a.id} className={styles.agenteItem}>
                      {a.photo && <img src={a.photo} alt="" className={styles.agentePhoto} />}
                      <span>{a.name} {a.last_name}</span>
                      {a.EventoAgente && (
                        <span className={styles.agenteStatus} data-status={a.EventoAgente.confirmado}>
                          {a.EventoAgente.confirmado === 'confirmado' ? '✅' : 
                           a.EventoAgente.confirmado === 'rechazado' ? '❌' : '⏳'}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {selectedEvent.creador && (
              <div className={styles.modalCreador}>
                Creado por: <strong>{selectedEvent.creador.name} {selectedEvent.creador.last_name}</strong>
              </div>
            )}

            {isAdmin && (
              <div className={styles.modalActions}>
                <button className={styles.editBtn} onClick={() => { setSelectedEvent(null); navigate(`/eventos/editar/${selectedEvent.id}`); }}>
                  ✏️ Editar evento
                </button>
                <select
                  className={styles.statusSelect}
                  value={selectedEvent.estado || 'pendiente'}
                  onChange={e => handleChangeStatus(selectedEvent.id, e.target.value)}
                >
                  {Object.entries(ESTADO_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
                <button
                  className={styles.deleteBtn}
                  onClick={() => handleDeleteEvent(selectedEvent.id)}
                  disabled={deleting === selectedEvent.id}
                >
                  {deleting === selectedEvent.id ? '...' : '🗑️ Eliminar'}
                </button>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;